/* eslint-disable eqeqeq */
/* eslint-disable no-magic-numbers */
/* eslint-disable complexity */
import { useState, useEffect, useRef } from 'preact/hooks';
import { createContext } from 'preact';
import PropTypes from 'prop-types';

import styleSheet from './styles/prediction.module.scss';
import SubmitButton from '../../components/shared-components/button/SubmitButton';
import { DataCaptureFormComponent as Form } from 'playbook-data-capture-sdk-web';
import { getMultiSelectOffset } from '../../helpers/formOffset';
import { getVotePercentages } from '../../polls/poll.helpers';
import { getPredictionSubmittedOptions } from '../predictions.helper';
import PredictionVoteScreen from './predictionVoteScreen';
import PredictionSubmittedScreen from './predictionSubmittedScreen';
import PredictionResolvedScreen from './predictionResolvedScreen';
import { useTranslation } from 'react-i18next';

import {
    getAuthHeader,
    checkForSignIn,
    getUserIdFromAuthHeader
} from '../../helpers/authentication';
import { InteractionEvent, getAnalyticsContext } from '@playbook/sdk-helpers';
import { getOrCreateEncodedUUID } from '../../helpers/user-meta';

const styles = styleSheet.locals || {};
export const PredictionContext = createContext({});

/**
 * Component which allows fan to vote for an option
 *
 * @param {object} props prediction option props
 * @param {string} props.prediction prediction data
 * @param {boolean} [props.isPreview] embed is a preview in admin UI
 * @param {string} props.transitionState transition state
 * @param {boolean} props.embeddedFormData form object
 * @param {object} props.container prediction embed container
 * @returns {Function} <PredictionContent />
 */
function PredictionContent({
    prediction,
    isPreview = false,
    transitionState,
    embeddedFormData,
    container
}) {
    const { t } = useTranslation();
    // use the above to control what happens on the results screen of predictions

    const isGated = prediction?.gated;
    const interactionEventRef = useRef(null);

    const predictionNotOpen =
        prediction.state === 'draft' || prediction.state === 'scheduled';
    const predictionClosed = prediction.state === 'closed';
    const predictionOpen = prediction.state === 'open';
    const predictionResolved = prediction.state === 'resolved';

    const [selectedOptions, setSelectedOptions] = useState([]);
    const [isSubmitted, setIsSubmitted] = useState(false);

    const predictionOptions = getVotePercentages(prediction);
    const transitionStyles = {
        entered: { opacity: 1, transform: 'rotate(0deg)' }
    };

    const submittedOptions = getPredictionSubmittedOptions(
        prediction.prediction_id
    );

    const [isSignedIn, setIsSignedIn] = useState(false);

    const authHeader = getAuthHeader();

    const [dataCaptureFormSubmitted, setDataCaptureFormSubmitted] =
        useState(false);

    const [goToPredictionSubmittedOnVote, setGoToPredictionSubmittedOnVote] =
        useState(false);

    const [revealResult, setRevealResult] = useState(false);

    const hasDataCaptureForm = embeddedFormData?.state === 'open';
    const formOffsetPixels = getMultiSelectOffset(embeddedFormData);

    if (!hasDataCaptureForm) {
        setGoToPredictionSubmittedOnVote(true);
    }

    const showPredictionResolved = hasDataCaptureForm
        ? revealResult && predictionResolved
        : predictionResolved;

    const userPrediction = localStorage.getItem(
        `fek-prediction-id-${prediction?.prediction_id}`
    );

    const showDataCaptureForm =
        predictionResolved || predictionClosed
            ? embeddedFormData?.state === 'open' &&
              hasDataCaptureForm &&
              !userPrediction &&
              !revealResult
            : embeddedFormData?.state === 'open' &&
              hasDataCaptureForm &&
              isSubmitted;

    useEffect(() => {
        setIsSignedIn(checkForSignIn());
    }, []);

    // Initialize InteractionEvent
    useEffect(() => {
        let userId = null;

        if (isGated && authHeader) {
            // Extract userId from JWT for gated predictions
            userId = getUserIdFromAuthHeader(authHeader);
        } else if (!isGated) {
            // Generate or retrieve userId from local storage for non-gated predictions
            userId = getOrCreateEncodedUUID();
        }

        const interactionEvent = new InteractionEvent({
            activationId: prediction?.prediction_id,
            userId,
            type: 'PREDICTION',
            isLoggedIn: !!authHeader,
            title: prediction?.text,
            context: getAnalyticsContext(container)
        });

        interactionEventRef.current = interactionEvent;

        if (typeof globalThis !== 'undefined') {
            globalThis.PLAYBOOK_CONFIG = {
                ...globalThis.PLAYBOOK_CONFIG,
                interactionEvent
            };
        }
    }, [prediction?.prediction_id, isGated, authHeader, container]);

    useEffect(() => {
        if (!isSubmitted && submittedOptions) {
            setIsSubmitted(true);
            setSelectedOptions(submittedOptions.split(','));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (isPreview && prediction.previewVoted) {
            setIsSubmitted(true);
            setSelectedOptions([prediction.questions[0].options[1].option_id]);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isPreview, prediction.previewVoted]);

    return (
        <PredictionContext.Provider
            value={{
                prediction,
                predictionOptions,
                predictionOpen,
                correctOptions: prediction.questions[0]?.correct_options || [],
                selectedOptions,
                setSelectedOptions,
                isSubmitted,
                setIsSubmitted,
                transitionStyles,
                isPreview,
                isSignedIn,
                setIsSignedIn,
                authHeader,
                isGated,
                interactionEvent: interactionEventRef.current
            }}
        >
            {predictionNotOpen && (
                <div
                    data-cy="openSooonMessage"
                    className={styles.predictionMessage}
                >
                    {t('predictions.openSoonMessage')}
                </div>
            )}

            {predictionClosed && !isSubmitted && (
                <div
                    data-cy="closedMessage"
                    className={styles.predictionMessage}
                >
                    {t('predictions.closedMessage')}
                </div>
            )}

            {showDataCaptureForm && !revealResult && (
                <div
                    style={
                        formOffsetPixels
                            ? {
                                  paddingBottom: formOffsetPixels
                              }
                            : {}
                    }
                >
                    <Form
                        formData={embeddedFormData}
                        submittedCallback={() => {
                            setDataCaptureFormSubmitted(true);
                        }}
                        predictionId={prediction.prediction_id}
                        analyticsContext={getAnalyticsContext(container)}
                    />

                    {dataCaptureFormSubmitted && (
                        <SubmitButton
                            onSubmit={() => {
                                setGoToPredictionSubmittedOnVote(true);
                                setRevealResult(true);
                            }}
                            buttonText={
                                revealResult
                                    ? t('predictions.formContinueToResult')
                                    : t('predictions.formContinue')
                            }
                            title="View prediction results"
                            buttonPart="predictor-submit-button"
                            loadingPart="predictor-submit-button-loading"
                        />
                    )}
                </div>
            )}

            {predictionOpen && !isSubmitted && (
                <PredictionVoteScreen transitionState={transitionState} />
            )}

            {isSubmitted &&
                !predictionResolved &&
                goToPredictionSubmittedOnVote && <PredictionSubmittedScreen />}

            {showPredictionResolved && <PredictionResolvedScreen />}
        </PredictionContext.Provider>
    );
}

PredictionContent.propTypes = {
    prediction: PropTypes.object.isRequired,
    isPreview: PropTypes.bool,
    transitionState: PropTypes.string.isRequired,
    embeddedFormData: PropTypes.object,
    container: PropTypes.node
};

export default PredictionContent;
